import { LogLevel, type Configuration } from '@azure/msal-browser';

/**
 * Valeur de build, la chaîne vide comptant pour une absence.
 *
 * Ces valeurs viennent de secrets GitHub Actions passés en `--build-arg` puis
 * inlinés par Vite. Un secret que l'on choisit de ne pas créer n'arrive donc pas
 * ici comme `undefined` mais comme chaîne VIDE, et `??` ne se déclenche pas.
 *
 * `VITE_AZURE_REDIRECT_URI` est précisément dans ce cas : on le laisse absent
 * pour qu'une seule image serve `hub.clippit.fr` et `hub-dev.clippit.fr`, MSAL
 * devant retomber sur l'hôte courant. Sans ce filtre il recevait `redirectUri:
 * ""` et le portail s'arrêtait sur « Failed to construct 'URL': Invalid URL ».
 */
const buildEnv = (value: string | undefined): string | undefined =>
    value && value.trim() !== '' ? value : undefined;

const clientId = buildEnv(import.meta.env.VITE_AZURE_CLIENT_ID) ?? '';
const tenantId = buildEnv(import.meta.env.VITE_AZURE_TENANT_ID) ?? '';

if (!clientId || !tenantId) {
    // Sans ces deux valeurs, la connexion échouerait avec un message obscur de
    // MSAL. Le signaler tôt évite une session de débogage inutile.
    console.error(
        'VITE_AZURE_CLIENT_ID et VITE_AZURE_TENANT_ID doivent être définis au moment du build.'
    );
}

export const msalConfig: Configuration = {
    auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId || 'common'}`,
        redirectUri:
            buildEnv(import.meta.env.VITE_AZURE_REDIRECT_URI) ?? window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        navigateToLoginRequestUrl: true,
    },
    cache: {
        // sessionStorage plutôt que localStorage : la session ne survit pas à la
        // fermeture de l'onglet, ce qui limite l'exposition sur un poste partagé.
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) return;
                if (level === LogLevel.Error) console.error(message);
                else if (level === LogLevel.Warning) console.warn(message);
            },
            logLevel: LogLevel.Warning,
        },
    },
};

/** Portées demandées à la connexion. */
export const loginRequest = {
    scopes: ['openid', 'profile', 'email', 'User.Read'],
};

/**
 * Portée du jeton d'accès présenté à l'API du Hub.
 *
 * L'API vérifie la signature, l'émetteur et l'audience de ce jeton : il doit
 * donc être émis pour elle, et non pour Microsoft Graph.
 */
export const apiRequest = {
    scopes: [
        buildEnv(import.meta.env.VITE_AZURE_API_SCOPE) ?? `api://${clientId}/access_as_user`,
    ],
};
